import { Map as ImmutableMap } from 'immutable';

import {
    NOTE_SIZE,
    SECTION_HEIGHT,
    SUB_SECTION_MARGIN,
    SUB_SECTION_HEIGHT,
    STORYBOARD_TOP_MARGIN,
    SLIDE_SECTION_HEIGHT, STORYBOARD_SECTION_MARGIN, CANVAS_TOP_MARGIN, CANVAS_SECTION_MARGIN
} from '../constants';
import { Locale } from './translation-store';

export interface Position {
    readonly anchor: 'LEFT' | 'RIGHT',
    readonly dx: number,
    readonly dy: number,
}

export interface MidPosition {
    readonly anchor: 'MIDDLE',
    readonly dy: number,
}

export interface SubSectionPosition {
    readonly anchor: 'LEFT' | 'RIGHT',
    readonly dx: number,
}

export interface LabelData {
    readonly texts: ReadonlyArray<string>,
    readonly size: 'SMALL' | 'MEDIUM' | 'LARGE',
    readonly anchor?: 'START' | 'MIDDLE' | 'END',
    readonly align?: 'TOP' | 'MIDDLE' | 'BOTTOM',
    readonly canvasPosition: Position | MidPosition,
}

export interface TextFieldData {
    readonly defaultValue: string,
    readonly key: string,
    readonly size: 'SMALL' | 'MEDIUM' | 'LARGE',
    readonly anchor?: 'START' | 'MIDDLE' | 'END',
    readonly align?: 'TOP' | 'MIDDLE' | 'BOTTOM',
    readonly canvasPosition: Position,
}

export interface HelpData {
    readonly button: LabelData,
    readonly title: string,
    readonly content: string,
}

export interface SubSectionData {
    readonly canvasPosition: SubSectionPosition,
    readonly labels?: ReadonlyArray<LabelData>,
    readonly help: HelpData,
}

export interface SectionData {
    readonly minWidth: number,
    readonly labels: ReadonlyArray<LabelData>,
    readonly textFields: ReadonlyArray<TextFieldData>,
    readonly subSections: ReadonlyArray<SubSectionData>,
    readonly help: HelpData,
}

export interface Template {
    readonly id: number,
    readonly title: string,
    readonly description: string,
    readonly thumb: string,
    readonly canvasWidth: number,
    readonly canvasHeight: number,
    readonly storyboardWidth: number,
    readonly storyboardHeight: number,
    readonly sections: ReadonlyArray<SectionData>,
    readonly texts: ImmutableMap<Locale, Map<string, string>>,
}


const EMPTY_TEMPLATE: Template = {
    id: -1,
    title: '',
    description: '',
    thumb: '',
    sections: [],
    canvasWidth: 1920,
    canvasHeight: 1080,
    storyboardWidth: 1920,
    storyboardHeight: 1080,
    texts: ImmutableMap<Locale, Map<string, string>>()
        .set('en', new Map())
        .set('de', new Map()),
};

export const CANVAS_WIDTH = 2.25 * 1920;

const TEMPLATES: ReadonlyArray<Template> = [
    {
        id: 1,
        title: 'overview_title',
        description: 'overview_description',
        thumb: 'images/template_thumb_general_3.png',
        canvasWidth: 1.85 * 1920,
        canvasHeight: 2 * CANVAS_TOP_MARGIN + 5 * SECTION_HEIGHT + 4 * CANVAS_SECTION_MARGIN,
        storyboardWidth: 7 * 1920,
        storyboardHeight: 2 * STORYBOARD_TOP_MARGIN + 5 * (SLIDE_SECTION_HEIGHT + SECTION_HEIGHT) + 4 * STORYBOARD_SECTION_MARGIN,
        sections: [
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['introduction_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['introduction_summary_1', 'introduction_summary_2', 'introduction_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_introduction',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'introduction_title',
                    content: 'introduction_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['opening_title'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'opening_title',
                            content: 'opening_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_1'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_1',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },

            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_2'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_2',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },

            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_3'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_3',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['conclusion_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['conclusion_summary_1', 'conclusion_summary_2', 'conclusion_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_conclusion',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'conclusion_title',
                    content: 'conclusion_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT + NOTE_SIZE},
                        labels: [{
                            texts: ['last_slide_title_1', 'last_slide_title_2'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 3 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'last_slide_title',
                            content: 'last_slide_help',
                        },
                    }
                ],
            },
        ],
        texts: ImmutableMap<Locale, Map<string, string>>()
            .set('de', createMap({
                title: 'Titel',
                more: 'mehr Infos',
                time: 'Dauer:',
                overview_title: 'Generelles Template mit drei Abschnitten im Hauptteil',
                overview_description: 'Dies ist ein generisches Template für Präsentation mit drei Abschnitten im Hauptteil.',
                opening_title: 'Eröffnung',
                opening_help: '',
                introduction_title: 'Einführung',
                introduction_summary_1: 'Was ist das Thema der Präsentation?',
                introduction_summary_2: 'Warum sollte sich das Publikum dafür interessieren?',
                introduction_summary_3: 'Warum soll es ausgerechnet Ihnen zuhören?',
                introduction_help: '',
                title_slide: 'Titelfolie',
                title_slide_help: '',
                body_title: 'Hauptteil',
                body_title_1: '1. Hauptpunkt',
                body_title_2: '2. Hauptpunkt',
                body_title_3: '3. Hauptpunkt',
                body_help: '',
                conclusion_title: 'Abschluss',
                conclusion_summary_1: 'Enden Sie mit Wucht',
                conclusion_summary_2: 'Fassen Sie Ihre Präsentation zusammen',
                conclusion_summary_3: 'Betonen Sie die Kernaussage',
                conclusion_summary_4: 'Rufen Sie zum Handeln auf',
                conclusion_help: '',
                last_slide_title_1: 'Letzte',
                last_slide_title_2: 'Folie',
                last_slide_help: '',
            })),
    },
    {
        id: 2,
        title: 'overview_title',
        description: 'overview_description',
        thumb: 'images/template_thumb_general_3.png',
        canvasWidth: 1.85 * 1920,
        canvasHeight: 2 * CANVAS_TOP_MARGIN + 6 * SECTION_HEIGHT + 5 * CANVAS_SECTION_MARGIN,
        storyboardWidth: 7 * 1920,
        storyboardHeight: 2 * STORYBOARD_TOP_MARGIN + 6 * (SLIDE_SECTION_HEIGHT + SECTION_HEIGHT) + 5 * STORYBOARD_SECTION_MARGIN,
        sections: [
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['introduction_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['introduction_summary_1', 'introduction_summary_2', 'introduction_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_introduction',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'introduction_title',
                    content: 'introduction_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['opening_title'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'opening_title',
                            content: 'opening_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_1'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_1',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },

            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_2'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_2',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_3'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_3',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_4'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_4',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['conclusion_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['conclusion_summary_1', 'conclusion_summary_2', 'conclusion_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_conclusion',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'conclusion_title',
                    content: 'conclusion_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT + NOTE_SIZE},
                        labels: [{
                            texts: ['last_slide_title_1', 'last_slide_title_2'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 3 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'last_slide_title',
                            content: 'last_slide_help',
                        },
                    }
                ],
            },
        ],
        texts: ImmutableMap<Locale, Map<string, string>>()
            .set('de', createMap({
                title: 'Titel',
                more: 'mehr Infos',
                time: 'Dauer:',
                overview_title: 'Generelles Template mit vier Abschnitten im Hauptteil',
                overview_description: 'Dies ist ein generisches Template für Präsentation mit vier Abschnitten im Hauptteil.',
                opening_title: 'Eröffnung',
                opening_help: '',
                introduction_title: 'Einführung',
                introduction_summary_1: 'Was ist das Thema der Präsentation?',
                introduction_summary_2: 'Warum sollte sich das Publikum dafür interessieren?',
                introduction_summary_3: 'Warum soll es ausgerechnet Ihnen zuhören?',
                introduction_help: '',
                title_slide: 'Titelfolie',
                title_slide_help: '',
                body_title: 'Hauptteil',
                body_title_1: '1. Hauptpunkt',
                body_title_2: '2. Hauptpunkt',
                body_title_3: '3. Hauptpunkt',
                body_title_4: '4. Hauptpunkt',
                body_help: '',
                conclusion_title: 'Abschluss',
                conclusion_summary_1: 'Enden Sie mit Wucht',
                conclusion_summary_2: 'Fassen Sie Ihre Präsentation zusammen',
                conclusion_summary_3: 'Betonen Sie die Kernaussage',
                conclusion_summary_4: 'Rufen Sie zum Handeln auf',
                conclusion_help: '',
                last_slide_title_1: 'Letzte',
                last_slide_title_2: 'Folie',
                last_slide_help: '',
            })),
    },
    {
        id: 3,
        title: 'overview_title',
        description: 'overview_description',
        thumb: 'images/template_thumb_general_3.png',
        canvasWidth: 1.85 * 1920,
        canvasHeight: 2 * CANVAS_TOP_MARGIN + 7 * SECTION_HEIGHT + 6 * CANVAS_SECTION_MARGIN,
        storyboardWidth: 7 * 1920,
        storyboardHeight: 2 * STORYBOARD_TOP_MARGIN + 7 * (SLIDE_SECTION_HEIGHT + SECTION_HEIGHT) + 6 * STORYBOARD_SECTION_MARGIN,
        sections: [
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['introduction_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['introduction_summary_1', 'introduction_summary_2', 'introduction_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_introduction',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'introduction_title',
                    content: 'introduction_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['opening_title'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'opening_title',
                            content: 'opening_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_1'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_1',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_2'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_2',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_3'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_3',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_4'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_4',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH * 2/3,
                labels: [
                    {
                        texts: ['body_title_5'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_body_5',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'body_title',
                    content: 'body_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'LEFT', dx: SUB_SECTION_MARGIN},
                        labels: [{
                            texts: ['title_slide'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 4 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'title_slide',
                            content: 'title_slide_help',
                        },
                    }
                ],
            },
            {
                minWidth: CANVAS_WIDTH / 2,
                labels: [
                    {
                        texts: ['conclusion_title'],
                        size: 'LARGE',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 3 / 10},
                    },
                    {
                        texts: ['conclusion_summary_1', 'conclusion_summary_2', 'conclusion_summary_3'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 4.5 / 10},
                    },
                    {
                        texts: ['time'],
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 4 / 10}
                    },
                ],
                textFields: [
                    {
                        defaultValue: '-:--',
                        key: 'time_conclusion',
                        size: 'MEDIUM',
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT / 2, dy: SECTION_HEIGHT * 6 / 10},
                    },
                ],
                help: {
                    button: {
                        texts: ['more'],
                        size: 'SMALL',
                        canvasPosition: {anchor: 'MIDDLE', dy: SECTION_HEIGHT * 9 / 10}
                    },
                    title: 'conclusion_title',
                    content: 'conclusion_help',
                },
                subSections: [
                    {
                        canvasPosition: {anchor: 'RIGHT', dx: SECTION_HEIGHT + NOTE_SIZE},
                        labels: [{
                            texts: ['last_slide_title_1', 'last_slide_title_2'],
                            size: 'SMALL',
                            canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 3 / 10}
                        }],
                        help: {
                            button: {
                                texts: ['more'],
                                size: 'SMALL',
                                canvasPosition: {anchor: 'MIDDLE', dy: SUB_SECTION_HEIGHT * 9 / 10}
                            },
                            title: 'last_slide_title',
                            content: 'last_slide_help',
                        },
                    }
                ],
            },
        ],
        texts: ImmutableMap<Locale, Map<string, string>>()
            .set('de', createMap({
                title: 'Titel',
                more: 'mehr Infos',
                time: 'Dauer:',
                overview_title: 'Generelles Template mit fünf Abschnitten im Hauptteil',
                overview_description: 'Dies ist ein generisches Template für Präsentation mit fünf Abschnitten im Hauptteil.',
                opening_title: 'Eröffnung',
                opening_help: '',
                introduction_title: 'Einführung',
                introduction_summary_1: 'Was ist das Thema der Präsentation?',
                introduction_summary_2: 'Warum sollte sich das Publikum dafür interessieren?',
                introduction_summary_3: 'Warum soll es ausgerechnet Ihnen zuhören?',
                introduction_help: '',
                title_slide: 'Titelfolie',
                title_slide_help: '',
                body_title: 'Hauptteil',
                body_title_1: '1. Hauptpunkt',
                body_title_2: '2. Hauptpunkt',
                body_title_3: '3. Hauptpunkt',
                body_title_4: '4. Hauptpunkt',
                body_title_5: '5. Hauptpunkt',
                body_help: '',
                conclusion_title: 'Abschluss',
                conclusion_summary_1: 'Enden Sie mit Wucht',
                conclusion_summary_2: 'Fassen Sie Ihre Präsentation zusammen',
                conclusion_summary_3: 'Betonen Sie die Kernaussage',
                conclusion_summary_4: 'Rufen Sie zum Handeln auf',
                conclusion_help: '',
                last_slide_title_1: 'Letzte',
                last_slide_title_2: 'Folie',
                last_slide_help: '',
            })),
    },
];


async function readTextFile(locale: Locale, key: string) {
    const response = await fetch(`${process.env.PUBLIC_URL}/text/${key}.${locale}.md`);
    return await response.text();
}

async function readTextFiles(locale: Locale) {
    const templates = TEMPLATES.filter(template => template.id === 1 && template.texts);
    for (const template of templates) {
        const texts = template.texts.get(locale);
        if (texts) {
            texts.set('opening_help', await readTextFile(locale, 'template_generic_opening_help'));
            texts.set('introduction_help', await readTextFile(locale, 'template_generic_introduction_help'));
            texts.set('title_slide_help', await readTextFile(locale, 'template_generic_title_slide_help'));
            texts.set('body_help', await readTextFile(locale, 'template_generic_body_help'));
            texts.set('conclusion_help', await readTextFile(locale, 'template_generic_conclusion_help'));
            texts.set('last_slide_help', await readTextFile(locale, 'template_generic_last_slide_help'));
        }
    }
}

export async function setupTemplateStore() {
    await readTextFiles('de');
}

function createMap(data: object): Map<string, string> {
    const result = new Map();
    Object.keys(data)
        .forEach(key => result.set(key, data[key]));
    return result;
}

export function getTemplates() {
    return TEMPLATES;
}

export function getTemplate(templateId: number) {
    const found = TEMPLATES.find(template => template.id === templateId);
    return found ? found : EMPTY_TEMPLATE;
}



// WEBPACK FOOTER //
// ./src/services/template-store.ts